import ConvertCerts from '@/utils/ConvertCerts'
import forge from 'node-forge'
import moment from 'moment-timezone'
import Metadata from '@/models/Metadata'

export default class {
  _convertCerts = new ConvertCerts()
  _metadata = null
  _publicCertificateFile = null
  _publicCertificate = null
  _publicCertificatePem = null
  _privateKey = null
  _privateKeyPem = null
  _publicModulus = '-1'
  _privateModulus = '-2'
  _passwordPrivateKey = ''
  _isMatchPrivateKeyAndPassword = false
  _isMatchPublicAndPrivateCerts = false
  _isFiel = false
  _isValid = false
  _hasError = false
  _isValidNotBefore = false
  _isValidNotAfter = false
  _errorMessage = null
  _errorMessages = [
    'La llave utilizada debe corresponder a una FIEL',
    'La combinación .key y contraseña no es correcta.',
    'La combinación de .cer, .key y contraseña no es correcta.',
    'La FIEL se encuentra caduca, favor de ingresar una FIEL válida.',
    'El RFC de la FIEL no coincide con su RFC: @'
  ]
  _rfcToCompare = null
  _isValidRfc = false

  constructor(cer, key, password, rfcToCompare) {
    this._publicCertificate = cer
    this._privateKey = key
    this._rfcToCompare = rfcToCompare
    this._publicCertificatePem = this._loadX509Certificate(cer)
    this._privateKeyPem = this._loadPkcs8Certificate(key)
    this._passwordPrivateKey = password
  }

  get metadata() {
    return this._metadata
  }

  get isValid() {
    return this._isValid
  }

  get isFiel() {
    return this._isFiel
  }

  get isMatchPublicAndPrivateCerts() {
    return this._isMatchPublicAndPrivateCerts
  }

  get isMatchPrivateKeyAndPassword() {
    return this._isMatchPrivateKeyAndPassword
  }

  get esVigente() {
    return this._isValidNotBefore && this._isValidNotAfter
  }

  get errorMessage() {
    return this._errorMessage
  }

  get certificateFile() {
    return this._publicCertificateFile
  }

  set certificateFile(value) {
    this._publicCertificateFile = value
  }

  get certificateBase64() {
    return this._publicCertificate
  }

  validate() {
    this._isMatchPrivateKeyAndPassword = this._matchPrivateCertAndPassword()
    this._isMatchPublicAndPrivateCerts = this._matchPublicAndPrivateCerts()

    this._isValid =
      this._isMatchPrivateKeyAndPassword &&
      this._isMatchPublicAndPrivateCerts &&
      this._isFiel &&
      this._isValidNotBefore &&
      this._isValidNotAfter &&
      this._isValidRfc

    if (!this._isValid) {
      this._setErrorMessage()
    }

    return this._isValid
  }

  _setErrorMessage() {
    this._errorMessage = this._errorMessages[this._getErrorMessageType()]
    if (this._getErrorMessageType() == 4) {
      this._errorMessage = this._errorMessage.replace('@', this._rfcToCompare)
    }
  }

  _getErrorMessageType() {
    if (!this._isFiel) return 0
    if (!this._isMatchPrivateKeyAndPassword) return 1
    if (!this._isMatchPublicAndPrivateCerts) return 2
    if (!(this._isValidNotBefore && this._isValidNotAfter)) return 3
    if (!this._isValidRfc) return 4
  }

  _loadX509Certificate(value) {
    if (value === undefined) {
      return null
    }

    const x509pem = this._convertCerts.x509ToPem(value)

    try {
      const x509Cert = forge.pki.certificateFromPem(x509pem)
      const keyUsage = x509Cert.getExtension('keyUsage')
      this._isFiel = keyUsage.dataEncipherment && keyUsage.keyAgreement
      this._publicModulus = x509Cert.publicKey.n.toString()

      let rfc = ''
      let razonSocial = ''
      let serialNumber = x509Cert.serialNumber

      const attributesLength = x509Cert.subject.attributes.length
      for (let index = 0; index < attributesLength; index++) {
        const element = x509Cert.subject.attributes[index]

        if (element.type === '2.5.4.45') {
          rfc = element.value.split('/')

          if (rfc.length > 0) {
            rfc = rfc[0].trim().toUpperCase()
          }
        }

        if (element.type === '2.5.4.41') {
          razonSocial = element.value.trim().toUpperCase()
        }
      }

      const alg = this._getAlgorithm(x509Cert.siginfo.algorithmOid)
      this._metadata = new Metadata(rfc, razonSocial, serialNumber, alg)

      const notBefore = moment(x509Cert.validity.notBefore)
      const notAfter = moment(x509Cert.validity.notAfter)
      const today = moment()

      this._isValidNotBefore = notBefore.diff(today, 'minutes') < 0
      this._isValidNotAfter = notAfter.diff(today, 'minutes') > 0
      this._isValidRfc = this._rfcToCompare == null || rfc == this._rfcToCompare

      return x509Cert
    } catch (error) {
      this._hasError = true
      this._errorMessage = error
      return null
    }
  }

  _loadPkcs8Certificate(value) {
    if (value === undefined) {
      return null
    }

    try {
      return this._convertCerts.pkcs8ToPem(value)
    } catch (error) {
      this._hasError = true
      this._errorMessage = error
      return null
    }
  }

  _matchPrivateCertAndPassword() {
    try {
      const privateKey = forge.pki.decryptRsaPrivateKey(
        this._privateKeyPem,
        this._passwordPrivateKey
      )

      if (privateKey !== null) {
        this._privateModulus = privateKey.n.toString()
        return true
      }

      return false
    } catch (error) {
      return false
    }
  }

  _matchPublicAndPrivateCerts() {
    return this._privateModulus === this._publicModulus
  }

  _getAlgorithm(oid) {
    let alg = null

    switch (oid) {
      case '1.2.840.113549.1.1.4':
        alg = 'MD5withRSA'
        break

      case '1.2.840.113549.1.1.5':
        alg = 'SHA1withRSA'
        break

      case '1.2.840.113549.1.1.14':
        alg = 'SHA224withRSA'
        break

      case '1.2.840.113549.1.1.11':
        alg = 'SHA256withRSA'
        break

      case '1.2.840.113549.1.1.12':
        alg = 'SHA384withRSA'
        break

      case '1.2.840.113549.1.1.13':
        alg = 'SHA512withRSA'
        break
    }
    return alg
  }
}
